import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';

import CustomInput from 'components/common/CustomInput/CustomInput';
import CustomPinInput from 'components/common/CustomPinInput/CustomPinInput';
import CustomSelect from 'components/common/CustomSelect';
import CustomButton, {
  defaultButtonStyle,
} from 'components/common/CustomButton';
import CustomTimePicker from 'components/common/CustomTimePicker';
import {
  getClubSettings,
  updateClubSettings,
  updateClubPin,
  emailClubPin,
} from 'api/clubApi';
import { getTimezones } from 'api/generalApi';
import PinInputModal from 'components/admin/Pin/PinInputModal';
import PinUpdateFormModal from 'components/admin/Pin/PinUpdateFormModal';
import ReplayDelayNote from 'components/admin/ReplayDelayNote';
import PointsRulesEditor from 'components/admin/PointsRulesEditor';
import CustomNewConfirm from 'components/NewDesign/CustomNewConfirm/CustomNewConfirm';
import { getErrorMessage } from 'utils/api';
import {
  clubFeaturesOf,
  meetTypeOptionsFor,
  organisationTitleOf,
  organisationWordOf,
} from 'utils/clubFeatures';
import { TEAM_LABEL_OPTIONS, teamLabelOf, teamWords } from 'utils/teamLabel';
import {
  clubSettingsSavedMessage,
  pointsRulesForOrganisation,
  pointsRulesProblems,
  tiesChangeWarning,
  toPointsRulesRequest,
} from 'utils/pointsRules';
import { updateClubs, updateSelectedClub } from 'services/user/userSlice';
import { useBlocker } from 'hooks/useChangeDetection';

import styles from './ClubSettings.module.css';
import routes from 'routes';

import AdminTablePageLayout from 'components/admin/AdminTablePageLayout';
import PageHeader from 'components/NewDesign/PageHeader';

// The page's values as one string, to tell whether it holds unsaved changes.
// The PIN is changed in its own dialog and saved at once, so it is left out.
const unsavedChangesKey = (values, recordAuthCodeRequired) =>
  JSON.stringify({ ...values, adminPIN: undefined, recordAuthCodeRequired });

const ClubSettings = () => {
  const { selectedClub, clubs } = useSelector((state) => state.user || {});
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // Only a club that has teams (a school) chooses a word for them.
  const hasTeams = clubFeaturesOf(selectedClub).teams;
  // A school is not offered season-best points.
  const seasonBestPoints = clubFeaturesOf(selectedClub).seasonBestPoints;
  // The page's words: "club" and "Club Settings", or "school" and "School Settings" for a school
  // (Doug, 2026-10-26, school words pass 1).
  const organisationWord = organisationWordOf(selectedClub);
  const organisationTitle = organisationTitleOf(selectedClub);

  const [formValues, setFormValues] = useState({
    clubName: '',
    clubLocation: '',
    clubDescription: '',
    defaultNewMeetType: '',
    defaultStartTime: '',
    defaultTimezone: '',
    defaultLocation: '',
    clubRecordAuthCode: '',
    // The club's points rules (utils/pointsRules.js), from the API.
    pointsRules: null,
    hidePRStarInClubResultsViews: false,
    adminPIN: '',
  });

  // Whether officials must enter an authorization code on the tablet to save a
  // club-record-breaking result. Server contract: code "0" (or empty) = not
  // required; 1000-9999 = required. The code itself lives in formValues.
  const [requireRecordAuthCode, setRequireRecordAuthCode] = useState(false);

  const [showPinInput, setShowPinInput] = useState(false);
  // The ties rule as saved, and the warning before a changed one is saved (slice 3, option B: it
  // places every meet of this season again; Doug, 2026-10-24: "lots of warning before they do it.
  // And PIN protect as well").
  const [savedSharedTies, setSavedSharedTies] = useState(null);
  const [showTiesWarning, setShowTiesWarning] = useState(false);
  const [showPinUpdate, setShowPinUpdate] = useState(false);
  const [showForgotPinConfirm, setShowForgotPinConfirm] = useState(false);
  const [emailPinLoading, setEmailPinLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pinUpdateLoading, setPinUpdateLoading] = useState(false);
  const [fetchingSettings, setFetchingSettings] = useState(true);
  const [pendingFormData, setPendingFormData] = useState(null);

  const [errors, setErrors] = useState({});
  const [timezoneOptions, setTimezoneOptions] = useState([]);

  // Unsaved changes (Doug, 2026-10-23: leaving the page lost twenty minutes
  // of points work without a word). The values as last loaded or saved; a
  // change since then asks before the page is left, as Edit Meet does
  // ("Discard Changes?"), and the browser asks before a reload or a closed
  // tab. The browser's Back button is not caught, as on Edit Meet.
  const [savedValuesKey, setSavedValuesKey] = useState(null);
  const [leaveAnyway, setLeaveAnyway] = useState(null);
  const hasUnsavedChanges =
    savedValuesKey !== null &&
    unsavedChangesKey(formValues, requireRecordAuthCode) !== savedValuesKey;

  const blockLeaving = useCallback(
    (retry) => {
      if (!hasUnsavedChanges) return true;
      setLeaveAnyway(() => retry);
      return false;
    },
    [hasUnsavedChanges]
  );
  useBlocker(blockLeaving, hasUnsavedChanges);

  useEffect(() => {
    if (!hasUnsavedChanges) return undefined;
    const warnBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [hasUnsavedChanges]);

  const confirmLeave = () => {
    const retry = leaveAnyway;
    setLeaveAnyway(null);
    if (retry) retry();
  };

  const fetchClubSetting = useCallback(async () => {
    if (!selectedClub?.clubID) {
      navigate(routes.admin.dashboard);
      return;
    }
    try {
      const response = await getClubSettings(selectedClub?.clubID);
      const rawCode = (response.data.clubRecordAuthCode ?? '').toString().trim();
      const codeRequired = rawCode !== '' && rawCode !== '0';
      setRequireRecordAuthCode(codeRequired);
      const loaded = {
        ...response.data,
        clubRecordAuthCode: codeRequired ? rawCode : '',
        // A school's hidden season-best points read as 0 and its first-result choice as ticked.
        pointsRules: pointsRulesForOrganisation(response.data.pointsRules, seasonBestPoints),
      };
      setFormValues(loaded);
      setSavedValuesKey(unsavedChangesKey(loaded, codeRequired));
      setSavedSharedTies(Boolean(response.data.pointsRules?.sharedTies));
    } catch (err) {
      console.log('[ClubSettings] Fetch club setting error: ', err);
      toast.error(`Failed to load ${organisationWord} settings`);
    } finally {
      setFetchingSettings(false);
    }
  }, [selectedClub?.clubID, navigate, organisationWord, seasonBestPoints]);

  useEffect(() => {
    fetchClubSetting();
  }, [fetchClubSetting]);

  // IANA timezone list for the "Default Timezone" picker (meets inherit this — §6.8 / D11).
  useEffect(() => {
    const loadTimezones = async () => {
      try {
        const res = await getTimezones();
        setTimezoneOptions(
          (res.data || []).map((tz) => ({ value: tz.id, label: tz.displayName }))
        );
      } catch (err) {
        console.log('[ClubSettings] Fetch timezones error: ', err);
      }
    };
    loadTimezones();
  }, []);



  // Handle scrolling to Achievement Points section when URL has hash
  useEffect(() => {
    const scrollToSection = () => {
      const element = document.getElementById('achievement-points');
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 300);
      }
    };

    if (window.location.hash === '#achievement-points') {
      if (fetchingSettings) {
        const timer = setTimeout(scrollToSection, 500);
        return () => clearTimeout(timer);
      } else {
        scrollToSection();
      }
    }
  }, [fetchingSettings]);


  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === 'checkbox' ? checked : value;

    // The record authorization code is a 4-digit number (1000-9999)
    if (name === 'clubRecordAuthCode') {
      newValue = value.replace(/\D/g, '').slice(0, 4);
    }

    setFormValues((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  const handlePointsRulesChange = (pointsRules) => {
    setFormValues((prev) => ({ ...prev, pointsRules }));
  };

  const handleTimeChange = (defaultStartTime) => {
    setFormValues({ ...formValues, defaultStartTime });
  };

  const handleRequireAuthCodeChange = (e) => {
    const { checked } = e.target;
    setRequireRecordAuthCode(checked);
    if (!checked) {
      setErrors((prev) => ({ ...prev, clubRecordAuthCode: undefined }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate Club Record Auth Code
    if (requireRecordAuthCode) {
      const code = Number(formValues.clubRecordAuthCode);
      if (
        !/^\d{4}$/.test(formValues.clubRecordAuthCode) ||
        code < 1000 ||
        code > 9999
      ) {
        newErrors.clubRecordAuthCode =
          'Authorization code must be a number between 1000 and 9999';
      }
    }

    // The points: the same checks as the server's (utils/pointsRules.js). The problems show in the
    // Points section's summary.
    const pointsProblems = pointsRulesProblems(formValues.pointsRules, {
      seasonBestPoints,
      relaysOffered: hasTeams,
    });
    if (pointsProblems.length > 0) {
      newErrors.pointsRules = pointsProblems[0];
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    // Validate form
    if (!validateForm()) {
      // The points message in Doug's words (2026-10-24, H6).
      toast.error(
        pointsRulesProblems(formValues.pointsRules, { seasonBestPoints, relaysOffered: hasTeams }).length > 0
          ? 'Please fix the points settings before saving: see the Points section.'
          : 'Please fix the validation errors before saving'
      );
      return;
    }

    // Store the current form data and show PIN input
    setPendingFormData({
      ...formValues,
      clubRecordAuthCode: requireRecordAuthCode
        ? formValues.clubRecordAuthCode
        : '0',
      defaultNewMeetType: +formValues.defaultNewMeetType,
      // Left out when the points never loaded, so the server keeps them as they are.
      pointsRules: formValues.pointsRules
        ? toPointsRulesRequest(formValues.pointsRules)
        : undefined,
      hidePRStarInClubResultsViews: formValues.hidePRStarInClubResultsViews,
      // The club's word for its teams. Left out for a club without teams, and
      // when the blank choice is picked, so the server keeps the stored word.
      teamLabel:
        hasTeams && formValues.teamLabel !== '' && formValues.teamLabel != null
          ? +formValues.teamLabel
          : undefined,
    });

    // A changed ties rule places this season's meets again: warn first, then the PIN.
    const tiesChanged =
      formValues.pointsRules &&
      savedSharedTies !== null &&
      Boolean(formValues.pointsRules.sharedTies) !== savedSharedTies;
    if (tiesChanged) {
      setShowTiesWarning(true);
    } else {
      setShowPinInput(true);
    }
  };

  const confirmTiesChange = () => {
    setShowTiesWarning(false);
    setShowPinInput(true);
  };

  const cancelTiesChange = () => {
    setShowTiesWarning(false);
    setPendingFormData(null);
  };

  // The warning's words for the rules being saved (utils/pointsRules.js): which way the ties rule is
  // changing, with this organisation's own 1st and 2nd place points.
  const tiesWarning = pendingFormData?.pointsRules ? tiesChangeWarning(pendingFormData.pointsRules) : null;

  const handlePinVerification = async (data) => {
    if (!pendingFormData) return;
    setLoading(true);
    try {
      const response = await updateClubSettings(selectedClub.clubID, pendingFormData);
      // After a changed ties rule the message is longer (how many meets changed): shown for 8 seconds,
      // as Edit Meet shows its longer message.
      const meetsPlacedAgain = response?.data?.meetsPlacedAgain;
      toast.success(
        clubSettingsSavedMessage(meetsPlacedAgain, organisationTitle),
        meetsPlacedAgain === null || meetsPlacedAgain === undefined ? undefined : { autoClose: 8000 }
      );
      if (pendingFormData.pointsRules) {
        setSavedSharedTies(Boolean(pendingFormData.pointsRules.sharedTies));
      }
      // What is on the page is now what is saved.
      setSavedValuesKey(unsavedChangesKey(formValues, requireRecordAuthCode));
      setShowPinInput(false);
      setPendingFormData(null);

      // The word for teams travels with the club, which is held in memory.
      // Put the new word there, so the menu and every page read it at once
      // and not at the next reload.
      const savedTeamLabel = pendingFormData.teamLabel;
      if (
        savedTeamLabel !== undefined &&
        savedTeamLabel !== teamLabelOf(selectedClub)
      ) {
        dispatch(
          updateSelectedClub({ ...selectedClub, teamLabel: savedTeamLabel })
        );
        dispatch(
          updateClubs(
            (clubs || []).map((club) =>
              club.clubID === selectedClub.clubID
                ? { ...club, teamLabel: savedTeamLabel }
                : club
            )
          )
        );
      }
    } catch (err) {
      console.log(err);
      // A refusal (400) says what to change, for example a points rule; anything else is a fault.
      toast.error(
        err?.response?.status === 400
          ? getErrorMessage(err)
          : `Failed to update ${organisationWord} settings`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(routes.admin.dashboard);
  };

  const closePinInput = () => {
    setShowPinInput(false);
    setPendingFormData(null);
  };

  const handleForgotPinConfirm = async () => {
    if (!selectedClub?.clubID) return;
    setEmailPinLoading(true);
    try {
      const response = await emailClubPin(selectedClub.clubID);
      toast.success(
        response?.data ||
          'Admin PIN has been emailed to your registered email address.'
      );
      setShowForgotPinConfirm(false);
    } catch (err) {
      console.log('[ClubSettings] Email PIN error: ', err);
      if (err?.response?.status === 403) {
        toast.error('You are not authorized to receive the Admin PIN by email.');
      } else {
        toast.error(getErrorMessage(err));
      }
    } finally {
      setEmailPinLoading(false);
    }
  };

  const handlePinUpdate = async (data) => {
    setPinUpdateLoading(true);
    try {
      await updateClubPin(selectedClub.clubID, data.currentPin, data.newPin);
      setFormValues({ ...formValues, adminPIN: data.newPin });
      setShowPinUpdate(false);
      toast.success('PIN updated successfully!');
    } catch (err) {
      console.log('[ClubSettings] PIN update error: ', err);
      toast.error(
        'Failed to update PIN. Please check your current PIN and try again.'
      );
    } finally {
      setPinUpdateLoading(false);
    }
  };

  if (fetchingSettings) {
    return (
      <AdminTablePageLayout loading={true}>
        <div>Loading...</div>
      </AdminTablePageLayout>
    );
  }


  return (
    <AdminTablePageLayout loading={loading}>
      <PageHeader
        title={`${organisationTitle} Settings`}
        subTitle={`Configure your ${organisationWord}'s default settings and preferences`}
        primarySubTitle
        regularWeightSubTitle
      >
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={handleCancel}
            style={{
              padding: '8px 16px',
              border: '1px solid #d1d5db',
              background: 'white',
              borderRadius: '6px',
              fontSize: '14px',
              cursor: 'pointer',
              color: '#374151',
            }}
          >
            Cancel
          </button>
        </div>
      </PageHeader>

      <div className={styles.formContainer}>
        {/* Club Information Card (School Information for a school) */}
        <div className={styles.formCard}>
          <h3 className={styles.cardTitle}>{organisationTitle} Information</h3>
          <p className={styles.cardSubtitle}>
            Update your {organisationWord}&apos;s basic information and details.
          </p>
          <div className={styles.formRow}>
            <CustomInput
              label={`${organisationTitle} Name`}
              name="clubName"
              value={formValues.clubName}
              onChange={handleInputChange}
            />
          </div>
          <div className={styles.formRow}>
            <CustomInput
              label="Location"
              name="clubLocation"
              value={formValues.clubLocation}
              onChange={handleInputChange}
            />
          </div>
          <div className={styles.formRow}>
            <CustomInput
              label="Description"
              name="clubDescription"
              value={formValues.clubDescription}
              onChange={handleInputChange}
            />
          </div>
          {hasTeams && (
            <div className={styles.formRow}>
              <CustomSelect
                label="We call our teams"
                name="teamLabel"
                options={TEAM_LABEL_OPTIONS}
                value={formValues.teamLabel ?? ''}
                onChange={handleInputChange}
                style={{ marginBottom: 0 }}
              />
              <div className={styles.helpText}>
                The word your {organisationWord} uses for its teams. It is the word used on
                the menu and on every page that shows them.
              </div>
            </div>
          )}
        </div>

        {/* Meet Settings Card */}
        <div className={styles.formCard}>
          <h3 className={styles.cardTitle}>Meet Settings</h3>
          <p className={styles.cardSubtitle}>
            Set the default values for Meets created in this {organisationWord}. These can be
            overridden when adding or editing a Meet.
          </p>
          <div className={styles.formGrid}>
            <CustomSelect
              label="Default Meet Type"
              options={meetTypeOptionsFor(selectedClub, 'form', formValues.defaultNewMeetType)}
              value={formValues.defaultNewMeetType}
              onChange={handleInputChange}
              name="defaultNewMeetType"
            />
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <CustomTimePicker
                label="Default Start Time"
                placeholder="00:00"
                error={errors.startTime}
                onTimeChange={handleTimeChange}
                value={formValues.defaultStartTime}
                style={{ marginBottom: '20px' }}
              />
              <div style={{ flex: 1, minWidth: '180px' }}>
                <CustomSelect
                  label="Default Timezone"
                  options={timezoneOptions}
                  value={formValues.defaultTimezone}
                  onChange={handleInputChange}
                  name="defaultTimezone"
                />
              </div>
            </div>
          </div>
          <div className={styles.formRow}>
            <CustomInput
              label="Default Meet Location"
              name="defaultLocation"
              value={formValues.defaultLocation}
              onChange={handleInputChange}
            />
          </div>
          <div className={styles.authCodeRow}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={requireRecordAuthCode}
                onChange={handleRequireAuthCodeChange}
                className={styles.checkbox}
              />
              <span>Require an authorization code when a result breaks a {organisationWord} record</span>
            </label>
            <p className={styles.fieldDescription} style={{ marginTop: '8px' }}>
              When checked, event officials must enter this code in the tablet app to save a field event attempt that breaks a {organisationWord} record.
              The result will be added to the <a href="/club-records" className={styles.descriptionLink}>Pending {organisationTitle} Records</a> list and will need to be approved by
              an admin before it replaces the current {organisationWord} record. When unchecked, there is no signoff required on the field, the result is added directly to the
              pending {organisationTitle} Records list for approval.
            </p>
            {requireRecordAuthCode && (
              <CustomInput
                label={`${organisationTitle} Record Authorization Code`}
                name="clubRecordAuthCode"
                value={formValues.clubRecordAuthCode}
                onChange={handleInputChange}
                placeholder="Enter code (1000-9999)"
                error={errors.clubRecordAuthCode}
                style={{ marginTop: '12px' }}
              />
            )}
          </div>
        </div>

        {/* Points: the club's points for every meet (docs/school-carnivals-points-slice-2.md in the
            portal repo). A meet can use its own (Edit Meet). */}
        <div className={`${styles.formCard} ${styles.anchorCard}`} id="achievement-points">
          <h3 className={styles.cardTitle}>Points</h3>
          <p className={styles.cardSubtitle}>
            The points your meets give. A meet can use different points when you add or edit it.
          </p>
          <PointsRulesEditor
            rules={formValues.pointsRules}
            onChange={handlePointsRulesChange}
            forClub
            seasonBestPoints={seasonBestPoints}
            hasHouses={hasTeams}
            teamWords={teamWords(
              formValues.teamLabel !== '' && formValues.teamLabel != null
                ? +formValues.teamLabel
                : teamLabelOf(selectedClub)
            )}
            idPrefix="club-points"
          />

          <ReplayDelayNote organisationWord={organisationWord} />
        </div>

        {/* Results View Settings Card */}
        <div className={styles.formCard}>
          <h3 className={styles.cardTitle}>Results View Settings</h3>
          <p className={styles.cardSubtitle}>
            Configure how results are displayed in {organisationWord} meet views.
          </p>
          <div className={styles.checkboxRow} style={{ marginTop: 0, borderTop: 'none', paddingTop: 0 }}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                name="hidePRStarInClubResultsViews"
                checked={formValues.hidePRStarInClubResultsViews}
                onChange={handleInputChange}
                className={styles.checkbox}
              />
              <span>Hide athlete PB stars in {organisationWord} meet results views</span>
            </label>
          </div>
          <p className={styles.checkboxDescription}>
            Applies to results views containing multiple athletes, PB stars are always displayed in individual athlete dashboards.
          </p>
        </div>

        {/* Security Card */}
        <div className={styles.formCard}>
          <h3 className={styles.cardTitle}>Security</h3>
          <p className={styles.cardSubtitle}>
            Certain admin operations that could be destructive or sensitive
            require a PIN code for confirmation.
          </p>
          <div className={styles.pinInputRow} style={{ position: 'relative' }}>
            <CustomPinInput
              label="Change Admin Pin Code"
              value={formValues.adminPIN}
              onEditClick={() => setShowPinUpdate(true)}
            />
            <div
              onClick={() => setShowPinUpdate(true)}
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '24px',
                height: '24px',
                zIndex: 10,
              }}
            ></div>
          </div>
          <button
            type="button"
            onClick={() => setShowForgotPinConfirm(true)}
            style={{
              marginTop: '8px',
              background: 'none',
              border: 'none',
              padding: 0,
              color: '#cf2c47',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            Forgot PIN?
          </button>
        </div>

        {/* Save Button - Centered on page */}
        <div
          style={{
            marginTop: '32px',
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <CustomButton
            style={{
              ...defaultButtonStyle,
              width: '160px',
            }}
            onClick={handleSave}
            disabled={loading || showPinInput}
          >
            {loading ? 'Saving...' : 'Save Settings'}
          </CustomButton>
        </div>
      </div>

      <PinInputModal
        clubId={selectedClub?.clubID}
        showPopup={showPinInput}
        closePopup={closePinInput}
        confirmPin={handlePinVerification}
        pinCode={formValues.adminPIN}
        description={`Changing ${organisationWord} settings is restricted to admin users with PIN code access. Enter the admin PIN to save your changes.`}
      />
      <PinUpdateFormModal
        isOpen={showPinUpdate}
        onClose={() => setShowPinUpdate(false)}
        onSave={handlePinUpdate}
        currentPin={formValues.adminPIN}
        loading={pinUpdateLoading}
      />
      <CustomNewConfirm
        isOpen={leaveAnyway !== null}
        onClose={() => setLeaveAnyway(null)}
        title="Discard Changes?"
        description="You have unsaved changes. Leave this page?"
        onConfirm={confirmLeave}
        confirmButtonText="OK"
        confirmButtonVariant="danger"
        onCancel={() => setLeaveAnyway(null)}
      />
      <CustomNewConfirm
        isOpen={showTiesWarning && Boolean(tiesWarning)}
        onClose={cancelTiesChange}
        onCancel={cancelTiesChange}
        title={tiesWarning?.title || ''}
        description={
          tiesWarning && (
            <div className={styles.tiesWarning}>
              <p>
                You are about to change <b>{tiesWarning.setting}</b> from <b>{tiesWarning.from}</b> to{' '}
                <b>{tiesWarning.to}</b>.
              </p>
              <p>{tiesWarning.intro}</p>
              <ul>
                <li>
                  <b>Places change.</b> {tiesWarning.places}
                </li>
                <li>
                  <b>Points change.</b> {tiesWarning.points}
                </li>
                <li>
                  <b>When.</b> {tiesWarning.when}
                </li>
              </ul>
              <p>{tiesWarning.closing}</p>
            </div>
          )
        }
        confirmButtonText={tiesWarning?.confirm}
        confirmButtonVariant="danger"
        onConfirm={confirmTiesChange}
      />
      <CustomNewConfirm
        isOpen={showForgotPinConfirm}
        onClose={() => setShowForgotPinConfirm(false)}
        title="Email Admin PIN"
        description="The current Admin PIN will be sent to your registered email address. Continue?"
        confirmButtonText={emailPinLoading ? 'Sending...' : 'Send PIN by Email'}
        onConfirm={handleForgotPinConfirm}
        isLoading={emailPinLoading}
      />
    </AdminTablePageLayout>
  );
};

export default ClubSettings;